import { PERMISSIONS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { taxes } from '../api/endpoints.js';
import { buildCreateTaxPayload, buildUpdateTaxPayload } from '../api/mutations.js';
import { taxFormInput, type TaxDetail, type TaxFormInput } from '../api/schemas.js';
import { TaxFields } from '../forms/tax-fields.js';

const CREATE_DEFAULTS: TaxFormInput = {
  propertyId: '',
  name: '',
  type: '',
  basis: 'PERCENTAGE',
  rate: '',
  amount: '',
  inclusive: false,
  priority: '0',
  compounds: false,
  appliesToFees: false,
  enabled: true,
};

function defaultsFrom(tax: TaxDetail): TaxFormInput {
  return {
    propertyId: tax.propertyId,
    name: tax.name,
    type: tax.type,
    basis: tax.basis,
    rate: tax.rate ?? '',
    amount: tax.amount ?? '',
    inclusive: tax.inclusive,
    priority: String(tax.priority),
    compounds: tax.compounds,
    appliesToFees: tax.appliesToFees,
    enabled: tax.enabled,
  };
}

export function TaxFormPage(): React.JSX.Element {
  const taxId = useParams().taxId;
  const isEdit = taxId !== undefined;
  return (
    <PermissionGuard
      permission={isEdit ? PERMISSIONS.TAX_UPDATE : PERMISSIONS.TAX_CREATE}
      fallback={<NotPermitted />}
    >
      {isEdit ? <EditTax id={taxId} /> : <CreateTax />}
    </PermissionGuard>
  );
}

function CreateTax(): React.JSX.Element {
  const navigate = useNavigate();
  const create = taxes.useCreate();
  const form = useAppForm<TaxFormInput>({
    schema: taxFormInput,
    defaultValues: CREATE_DEFAULTS,
    successMessage: 'Tax created',
    onSubmit: async (values) => {
      const result = await create.mutateAsync(buildCreateTaxPayload(values));
      void navigate(`/taxes/${result.id}`);
    },
  });
  return (
    <FormPage
      title="New tax"
      description="Add a tax or service charge to a property."
      backTo="/taxes"
      backLabel="Taxes"
      submitLabel="Create tax"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <TaxFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        isEdit={false}
      />
    </FormPage>
  );
}

function EditTax({ id }: { readonly id: string }): React.JSX.Element {
  const query = taxes.useItem(id);
  if (query.isPending) return <LoadingState label="Loading tax" />;
  if (query.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => void query.refetch()}
      />
    );
  }
  return <EditTaxForm id={id} tax={query.data} />;
}

function EditTaxForm({
  id,
  tax,
}: {
  readonly id: string;
  readonly tax: TaxDetail;
}): React.JSX.Element {
  const navigate = useNavigate();
  const update = taxes.useUpdate();
  const form = useAppForm<TaxFormInput>({
    schema: taxFormInput,
    defaultValues: defaultsFrom(tax),
    successMessage: 'Tax updated',
    onSubmit: async (values) => {
      await update.mutateAsync({
        id,
        payload: buildUpdateTaxPayload(values, tax.version),
      });
      void navigate(`/taxes/${id}`);
    },
  });
  return (
    <FormPage
      title={`Edit ${tax.name}`}
      backTo={`/taxes/${id}`}
      backLabel="Tax"
      submitLabel="Save changes"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <TaxFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        isEdit
      />
    </FormPage>
  );
}
